import { useRef, useState } from "react";
import { Gauge, Film, Loader2, Sparkles, TrendingUp, CheckCircle2, AlertCircle, Clock, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const scoreColor = (s) => (s >= 70 ? "#22c55e" : s >= 45 ? "#f59e0b" : "#ef4444");

function ScoreRing({ score, size = 64, label }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  const col = scoreColor(score);
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} stroke="currentColor" strokeWidth="6" fill="none" className="text-neutral-700" />
          <circle cx={size / 2} cy={size / 2} r={r} stroke={col} strokeWidth="6" fill="none"
            strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.7s ease" }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-base font-bold" style={{ color: col }}>{score}</span>
      </div>
      {label && <span className="text-[10px] uppercase tracking-wider text-neutral-400">{label}</span>}
    </div>
  );
}

// KI-Erfolgsprognose (Virality Score) + multimodale Video-Analyse
export function AiInsightPanel({ accountKey, content }) {
  const [vir, setVir] = useState(null);
  const [virBusy, setVirBusy] = useState(false);
  const [vid, setVid] = useState(null);
  const [vidBusy, setVidBusy] = useState(false);
  const fileRef = useRef(null);

  const runVirality = async () => {
    setVirBusy(true);
    try {
      const { data } = await api.post("/ai/virality", {
        account_key: accountKey,
        title: content.title || "",
        hook: content.hook || "",
        caption: content.caption || "",
        cta: content.cta || "",
        content_type: content.content_type || "reel",
      });
      setVir(data);
      if (data.data_backed) toast.success("Prognose auf Basis echter Performance-Daten");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erfolgsprognose fehlgeschlagen");
    } finally {
      setVirBusy(false);
    }
  };

  const runVideo = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setVidBusy(true);
    setVid(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ai/video-analysis", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVid(data);
      toast.success("Video analysiert");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Video-Analyse fehlgeschlagen");
    } finally {
      setVidBusy(false);
    }
  };

  return (
    <div data-testid="ai-insight-panel" className="rounded-xl border border-neutral-700 bg-neutral-800/40 p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-indigo-400" /> KI-Erfolgs-Tools
        </p>
        <div className="flex gap-2">
          <button data-testid="btn-virality" onClick={runVirality} disabled={virBusy}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white hover:opacity-90 transition disabled:opacity-60">
            {virBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gauge className="h-3.5 w-3.5" />} Erfolgsprognose
          </button>
          <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" onChange={runVideo} className="hidden" data-testid="video-analysis-input" />
          <button data-testid="btn-video-analysis" onClick={() => fileRef.current?.click()} disabled={vidBusy}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-neutral-700 text-neutral-100 hover:bg-neutral-600 transition disabled:opacity-60">
            {vidBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />} Video analysieren
          </button>
        </div>
      </div>

      {/* Virality result */}
      {vir && (
        <div data-testid="virality-result" className="flex gap-4 items-start rounded-lg bg-neutral-900/60 border border-neutral-700 p-4">
          <ScoreRing score={vir.score} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-white">Virality-Score</span>
              {vir.predicted_band && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-300 border border-neutral-700">
                  Reichweite: {vir.predicted_band}
                </span>
              )}
              {vir.best_time && (
                <span className="text-[11px] text-neutral-400 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {vir.best_time}</span>
              )}
            </div>
            {vir.verdict && <p className="text-[13px] text-neutral-300 mt-1">{vir.verdict}</p>}
            {vir.strengths?.length > 0 && (
              <ul className="mt-2 space-y-1">
                {vir.strengths.map((s, i) => (
                  <li key={i} className="text-[12px] text-emerald-300 flex items-start gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {s}</li>
                ))}
              </ul>
            )}
            {vir.improvements?.length > 0 && (
              <ul className="mt-2 space-y-1">
                {vir.improvements.map((s, i) => (
                  <li key={i} className="text-[12px] text-amber-300 flex items-start gap-1.5"><TrendingUp className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {s}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Video analysis result */}
      {vid && (
        <div data-testid="video-result" className="rounded-lg bg-neutral-900/60 border border-neutral-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="h-4 w-4 text-indigo-400" />
            <span className="text-sm font-semibold text-white">Video-Analyse</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <ScoreRing score={vid.overall_score} size={56} label="Gesamt" />
            <ScoreRing score={vid.hook_score} size={56} label="3-Sek-Hook" />
            <ScoreRing score={vid.pacing_score} size={56} label="Tempo" />
            <ScoreRing score={vid.cut_quality} size={56} label="Schnitt" />
          </div>
          {vid.hook_feedback && (
            <p className="text-[12px] text-neutral-300 mt-3"><span className="font-semibold text-white">Hook: </span>{vid.hook_feedback}</p>
          )}
          {vid.observations?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {vid.observations.map((s, i) => (
                <li key={i} className="text-[12px] text-neutral-300 flex items-start gap-1.5"><AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-neutral-500" /> {s}</li>
              ))}
            </ul>
          )}
          {vid.recommendations?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {vid.recommendations.map((s, i) => (
                <li key={i} className="text-[12px] text-amber-300 flex items-start gap-1.5"><TrendingUp className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {s}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!vir && !vid && !virBusy && !vidBusy && (
        <p className="text-[12px] text-neutral-500">
          Lass die KI das virale Potenzial dieses Beitrags einschätzen — oder lade einen Clip hoch für Schnitt-/Hook-Feedback (max. 50 MB).
        </p>
      )}
    </div>
  );
}
